import { inject, Injectable, Signal, signal } from '@angular/core';
import { APP_CONFIG } from '../config/app-config';
import { HttpClient } from '@angular/common/http';
import { LocalStorageService } from './local-storage.service';
import { DriverName } from '../base/entities/driver-name';
import { catchError, tap } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { User } from '../base/authentication/user';

@Injectable({
  providedIn: 'root'
})
export class DriverNameService {
  private static readonly DRIVER_NAME_KEY = 'driverName';

  private readonly appConfig = inject(APP_CONFIG);
  private readonly httpClient = inject(HttpClient);
  private readonly localStorageService = inject(LocalStorageService);

  private readonly writableDriverName = signal<DriverName | null>(null);
  public readonly driverName: Signal<DriverName | null> = this.writableDriverName.asReadonly();

  private readonly writableIsInitialized = signal<boolean>(false);
  public readonly isInitialized: Signal<boolean> = this.writableIsInitialized.asReadonly();

  /**
   * Ensure a driver name is loaded before {@link UserService} calls GetUser and fetches user settings.
   * Actual validation of this driver name is done in {@link initialize}.
   */
  constructor() {
    this.setDriverName(this.getPersistedDriverName());
  }

  /**
   * Validate if the stored {@link DriverName} is authorized for use by the current {@link User}.
   * For example when a developer switches driver account or if a driver name was deleted.
   */
  initialize() {
    return this.getDriverNames().pipe(
      tap((driverNames) => {
        const authorizedDriverName = driverNames.find((driverName) => driverName.id === this.driverName()?.id) ?? null;
        this.setDriverName(authorizedDriverName);
        this.writableIsInitialized.set(true);
      }),
      catchError((error) => {
        this.clear();
        return throwError(() => error);
      })
    );
  }

  private getPersistedDriverName() {
    return this.localStorageService.get<DriverName | null>(DriverNameService.DRIVER_NAME_KEY);
  }

  getDriverNames() {
    return this.httpClient.get<DriverName[]>(`${this.appConfig.newApiUrl}api/GetDriverNames`);
  }

  createDriverName(firstName: string, lastName: string) {
    return this.httpClient.post<DriverName>(`${this.appConfig.newApiUrl}api/CreateDriverName`, {
      firstName,
      lastName
    });
  }

  setDriverName(driverName: DriverName | null): void {
    this.localStorageService.set(DriverNameService.DRIVER_NAME_KEY, driverName);
    this.writableDriverName.set(driverName);
  }

  clear() {
    this.localStorageService.delete(DriverNameService.DRIVER_NAME_KEY);
    this.writableDriverName.set(null);
  }
}
